<template>
  <div>
    <!-- Read-only visning: ikoner eller tekst -->
    <div v-if="readOnly" class="center-align">
      <template v-if="showText">
        <span v-if="modelValue === true">Sann</span>
        <span v-else-if="modelValue === false">Usann</span>
        <span v-else class="muted">(blank)</span>
      </template>
      <template v-else>
        <font-awesome-icon v-if="modelValue === true" :icon="['fal', 'check']"/>
        <font-awesome-icon v-else-if="modelValue === false" :icon="['fal', 'minus']"/>
        <font-awesome-icon v-else :icon="['fal', 'question']"/>
      </template>
    </div>

    <!-- Edit mode: tri-state toggle for nullable bool -->
    <template v-else-if="header.isNullable">
      <BaseToggleGroup
        :model-value="booleanValue"
        :options="triStateOptions"
        :allow-null="true"
        :class="{ invalid: hasError }"
        class="compact-toggle"
        @update:model-value="changed"
      />
      <BaseProgressBar v-if="saving" :value="null" class="saving-progress" />
      <div v-else style="height: 4px;padding: 0;margin: 0;">
        &nbsp;
      </div>
      <span v-if="hasError" style="color: var(--red-700)">Feil ved lagring!!!</span>
    </template>

    <!-- Edit mode: switch for non-nullable bool -->
    <template v-else>
      <BaseOnOff :model-value="booleanValue" :class="{invalid: hasError}" prevent-center @change="changed" />
      <BaseProgressBar v-if="saving" :value="null" class="saving-progress" />
      <div v-else style="height: 4px;padding: 0;margin: 0;">
        &nbsp;
      </div>
      <span v-if="hasError" style="color: var(--red-700)">Feil ved lagring!!!</span>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useTableStore } from '@/store/storeTable'
import BaseOnOff from '@/components/BaseComponents/BaseOnOff.vue'
import BaseToggleGroup from '@/components/BaseComponents/BaseToggleGroup.vue'
import BaseProgressBar from '@/components/BaseComponents/BaseProgressBar.vue'

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Array, Date, Object],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  showText: {
    type: Boolean,
    default: false
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true }
});


const emit = defineEmits(['update:modelValue'])


const hasError = ref(false)
const saving = ref(false)

// TableStore
const tableStore = useTableStore()

// Tri-state options for nullable bool
const triStateOptions = [
  { value: true, label: 'Ja' },
  { value: false, label: 'Nei' },
  { value: null, label: 'Ukjent' }
]

// Konverter modelValue til boolean for BaseOnOff
// BaseOnOff forventer Boolean | null, men modelValue kan være string/number fra columnDefault
const booleanValue = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) {
    // For non-nullable bools, konverter null til false
    if (!props.header.isNullable) {
      return false;
    }
    return null;
  }
  if (typeof props.modelValue === 'boolean') {
    return props.modelValue;
  }
  if (typeof props.modelValue === 'string') {
    const lower = props.modelValue.toLowerCase().trim();
    if (lower === '1' || lower === 'true') return true;
    if (lower === '0' || lower === 'false' || lower === '') return false;
    return Boolean(props.modelValue);
  }
  if (typeof props.modelValue === 'number') {
    return props.modelValue !== 0;
  }
  // For andre typer (Array, Date, Object), returner null
  return null;
});

async function changed (newValue) {
  // For non-nullable bools, konverter null til false
  const finalValue = (!props.header.isNullable && (newValue === null || newValue === undefined)) ? false : newValue;
  emit('update:modelValue', finalValue)

  if (!props.autoSave) return;

  const item = { rowId: props.rowId, databaseObjectId: props.header.databaseObjectId, columnName: props.header.field, oldValue: props.modelValue, newValue: finalValue };
  saving.value = true
  hasError.value = false
  try {
    await tableStore.saveData(item);
  } catch (error) {
    hasError.value = true
    console.error('Feil ved lagring av bool-verdi:', error)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.saving-progress {
  height: 4px;
  padding: 0;
  margin: 0;
  color: lightgreen;
}

.center-align {
  text-align: center;
}

.muted {
  color: var(--color-text-muted);
  font-style: italic;
}

/* Kompakt styling for tri-state toggle i editBool */
.compact-toggle :deep(.toggle-group) {
  gap: 0.25rem;
}

.compact-toggle :deep(.toggle-option) {
  min-width: 70px;
  padding: 0.375rem 0.5rem;
  font-size: 0.85rem;
}

.invalid :deep(.toggle-group) {
  border: 1px solid var(--red-700, #c53030);
  border-radius: 6px;
  padding: 2px;
}
</style>
